"use client";
import React, { useEffect, useRef } from "react";
import styles from "./MissionTimeline.module.css";
import { aiChatBotPhases as PHASES } from "../../data/ai-chat-bot";

export default function MissionTimeline() {
  const containerRef = useRef();
  const lineRef = useRef();
  const observers = useRef([]);

  useEffect(() => {
    const container = containerRef.current;
    const line = lineRef.current;
    if (!container || !line) return;

    // Draw the vertical line as user scrolls: use IntersectionObserver on waypoints
    const opts = { root: null, rootMargin: "0px", threshold: 0.15 };

    const waypointEls = Array.from(
      container.querySelectorAll(`.${styles.waypoint}`)
    );

    function onIntersect(entries) {
      entries.forEach((entry) => {
        const el = entry.target;
        const id = el.getAttribute("data-phase");
        const index = PHASES.findIndex((p) => p.id === id);
        if (entry.isIntersecting) {
          el.classList.add(styles.active);
          // extend line to this point (by percentage)
          const pct = ((index + 1) / PHASES.length) * 100;
          line.style.setProperty("--draw-pct", `${pct}%`);

          // reveal all content blocks for this phase (both left and right)
          const contents = Array.from(
            container.querySelectorAll(`.${styles.content}[data-phase='${id}']`)
          );
          contents.forEach((c) => c.classList.add(styles.show));
        }
      });
    }

    const obs = new IntersectionObserver(onIntersect, opts);
    waypointEls.forEach((w) => obs.observe(w));
    observers.current.push(obs);

    return () => {
      observers.current.forEach((o) => o.disconnect());
      observers.current = [];
    };
  }, []);

  return (
    <section className={styles.section} aria-labelledby="mission-title">
      <div className="container">
        <div className="text-center mb-4" data-reveal="fade">
          <p className={styles.eyebrow}>Our Process</p>
          <h2 id="mission-title" className={styles.title}>
            Our Process, Your Success
          </h2>
          <p className={styles.sub}>
            From initial concept to a fully optimized AI, we’ve perfected a
            transparent, four-phase process to ensure a seamless launch and
            lasting impact.
          </p>
        </div>

        <div className={styles.timelineWrap} ref={containerRef}>
          <div className={styles.centerLine} aria-hidden>
            <div className={styles.drawn} ref={lineRef} />
          </div>

          <div className={styles.items}>
            {PHASES.map((p, i) => (
              <div className={styles.row} key={p.id}>
                {p.side === "left" ? (
                  <div
                    className={`${styles.content} ${styles.left}`}
                    data-phase={p.id}
                  >
                    <div className={styles.card} tabIndex={0}>
                      <h3 className={styles.cardTitle}>{p.title}</h3>
                      <p className={styles.cardDesc}>{p.desc}</p>
                      <ul className={styles.cardList}>
                        {p.bullets.map((b) => (
                          <li key={b}>{b}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ) : (
                  <div
                    className={`${styles.content} ${styles.left}`}
                    aria-hidden
                  />
                )}

                <div className={styles.centerPointWrap}>
                  <div
                    className={styles.waypoint}
                    data-phase={p.id}
                    aria-hidden
                  >
                    <div className={styles.icon} aria-hidden>
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="6"
                          stroke="#ff8f3a"
                          strokeWidth="1.2"
                          strokeOpacity="0.96"
                          fill="none"
                        />
                        <path
                          d="M8 12 L12 16 L16 10"
                          stroke="#fff"
                          strokeWidth="1.1"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          fill="none"
                          strokeOpacity="0.96"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                {p.side === "right" ? (
                  <div
                    className={`${styles.content} ${styles.right}`}
                    data-phase={p.id}
                  >
                    <div className={styles.card} tabIndex={0}>
                      <h3 className={styles.cardTitle}>{p.title}</h3>
                      <p className={styles.cardDesc}>{p.desc}</p>
                      <ul className={styles.cardList}>
                        {p.bullets.map((b) => (
                          <li key={b}>{b}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ) : (
                  <div
                    className={`${styles.content} ${styles.right}`}
                    aria-hidden
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
